import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    Typography,
    Button,
    Box,
    IconButton,
} from '@mui/material';
import { Close as CloseIcon, NewReleases as NewReleasesIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import versionData from '../../version.json';

const ChangelogModal = ({ open, version, onClose, onCloseAndDontShowAgain }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'es';
    const changelogContent = versionData.messages?.[lang] || versionData.messages?.es || '';

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
                    maxHeight: '85vh',
                    width: '90vw',
                    maxWidth: '850px',
                }
            }}
        >
            {/* Header with title */}
            <DialogTitle component="div" sx={{
                textAlign: 'center',
                pt: 2,
                pb: 1.5,
                position: 'relative',
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5
            }}>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'primary.light',
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                >
                    <NewReleasesIcon sx={{ fontSize: 22 }} />
                </Box>

                <Typography variant="h6" component="h2" fontWeight="bold">
                    {t('changelog.title', { version })}
                </Typography>
            </DialogTitle>

            {/* Content - Two columns */}
            <DialogContent sx={{ p: 2, overflow: 'auto' }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'row', 
                    gap: 2,
                    height: '100%'
                }}>
                    {/* Left column - Thanks */}
                    <Box sx={{
                        flex: '0 0 35%',
                        bgcolor: 'info.lighter',
                        borderRadius: 2,
                        p: 2,
                        borderLeft: '4px solid',
                        borderColor: 'info.main',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t('changelog.thanks')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('changelog.bug_report_reminder')}
                        </Typography>
                    </Box>

                    {/* Right column - Changes */}
                    <Box sx={{
                        flex: 1,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        p: 2,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        overflow: 'auto'
                    }}>
                        <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{
                                whiteSpace: 'pre-line',
                                lineHeight: 1.6
                            }}
                        >
                            {changelogContent}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            {/* Footer - Buttons */}
            <DialogActions sx={{ 
                flexDirection: 'row', 
                gap: 2, 
                p: 2, 
                pt: 1.5,
                justifyContent: 'flex-end',
                borderTop: '1px solid rgba(0,0,0,0.08)'
            }}>
                <Button
                    variant="text"
                    onClick={onClose}
                    sx={{
                        borderRadius: 2,
                        color: 'text.secondary',
                        px: 3
                    }}
                >
                    {t('changelog.close')}
                </Button>

                <Button
                    variant="contained"
                    size="medium"
                    onClick={onCloseAndDontShowAgain}
                    sx={{
                        borderRadius: 2,
                        py: 1,
                        px: 3,
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
                    }}
                >
                    {t('changelog.close_and_dont_show')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangelogModal;
